import { defineStyle, defineStyleConfig } from "@chakra-ui/react";
import textStyles from "../../../../theme/foundations/generated/textStyles";
import { ButtonVariant } from "../Button.types";
const baseStyle = defineStyle({
    ".chakra-button__icon": {
        margin: 0,
    },
    border: "3px solid",
    borderRadius: "none",
    _disabled: {
        opacity: 1,
    },
    _focusVisible: {
        boxShadow: "none",
        outline: "3px solid",
        outlineColor: "semantic.outline.focus",
        _hover: {
            boxShadow: "none",
        },
    },
    _loading: {
        background: "semantic.cta.primaryHover",
    },
    _hover: {
        boxShadow: "large",
        _loading: {
            background: "semantic.cta.primaryHover",
        },
        _disabled: {
            boxShadow: "none",
        },
    },
    _active: {
        boxShadow: "medium",
    },
});
const sizes = {
    lg: defineStyle(Object.assign({ px: "6", py: "3", ".chakra-button__icon": {
            height: "6",
        } }, textStyles.body.largeBold)),
    md: defineStyle(Object.assign({ px: "4", py: "2", ".chakra-button__icon": {
            height: "6",
        } }, textStyles.body.mediumBold)),
    sm: defineStyle(Object.assign({ px: "4", py: "2", ".chakra-button__icon": {
            height: "4",
        } }, textStyles.body.smallBold)),
};
const variants = {
    chamfered: defineStyle({
        background: "semantic.cta.primary",
        _hover: {
            background: "semantic.cta.primaryHover",
            _disabled: {
                background: "semantic.background.disabled",
                color: "semantic.text.disabled",
            },
        },
        _active: {
            background: "semantic.cta.primaryPressed",
            color: "semantic.text.inverse",
        },
        _disabled: {
            background: "semantic.background.disabled",
            color: "semantic.text.disabled",
        },
        _focusVisible: {
            background: "semantic.cta.primaryHover",
        },
    }),
    primaryFilled: defineStyle({
        background: "semantic.cta.primary",
        borderColor: "semantic.foreground.primary",
        color: "semantic.text.primary",
        _loading: {
            borderColor: "semantic.foreground.primary",
        },
        _hover: {
            borderColor: "semantic.foreground.primary",
            background: "semantic.cta.primaryHover",
            _disabled: {
                borderColor: "semantic.border.disabled",
                background: "semantic.background.disabled",
                color: "semantic.text.disabled",
            },
            _loading: {
                borderColor: "semantic.foreground.primary",
            },
        },
        _focusVisible: {
            borderColor: "semantic.foreground.primary",
            background: "semantic.cta.primaryHover",
        },
        _active: {
            borderColor: "semantic.cta.primaryPressed",
            background: "semantic.cta.primaryPressed",
            color: "semantic.text.inverse",
        },
        _disabled: {
            borderColor: "semantic.border.disabled",
            background: "semantic.background.disabled",
            color: "semantic.text.disabled",
        },
    }),
    secondaryFilled: defineStyle({
        background: "semantic.cta.secondary",
        border: "none",
        color: "semantic.text.inverse",
        _loading: {
            background: "semantic.cta.secondaryHover",
        },
        _hover: {
            background: "semantic.cta.secondaryHover",
            color: "semantic.text.primary",
            _disabled: {
                background: "semantic.background.disabled",
                color: "semantic.text.disabled",
            },
            _loading: {
                background: "semantic.cta.secondaryHover",
            },
        },
        _focusVisible: {
            background: "semantic.cta.secondaryHover",
            color: "semantic.text.primary",
        },
        _active: {
            background: "semantic.cta.secondaryPressed",
            color: "semantic.text.inverse ",
        },
        _disabled: {
            background: "semantic.background.disabled",
            color: "semantic.text.disabled",
        },
    }),
    icon: defineStyle({
        border: "none",
        color: "semantic.cta.secondary",
        _loading: {
            background: "semantic.cta.secondaryHover",
        },
        _hover: {
            boxShadow: "none",
            color: "semantic.text.primary",
            _disabled: {
                color: "semantic.text.disabled",
            },
            _loading: {
                background: "semantic.cta.secondaryHover",
            },
        },
        _focusVisible: {
            color: "semantic.text.primary",
        },
        _active: {
            boxShadow: "none",
            color: "semantic.cta.secondaryPressed",
        },
        _disabled: {
            color: "semantic.text.disabled",
        },
    }),
    deleteIcon: defineStyle({
        border: "none",
        backgroundColor: "semantic.brand.primary",
        color: "semantic.text.inverse",
        _loading: {
            background: "semantic.cta.secondaryHover",
        },
        _hover: {
            backgroundColor: "semantic.brand.primary",
            boxShadow: "none",
            color: "semantic.text.inverse",
            _disabled: {
                color: "semantic.text.disabled",
            },
            _loading: {
                background: "semantic.cta.secondaryHover",
            },
        },
        _focusVisible: {
            color: "semantic.text.inverse",
        },
        _active: {
            color: "semantic.text.inverse",
            boxShadow: "none",
        },
        _disabled: {
            color: "semantic.text.disabled",
        },
    }),
    notificationDeleteIcon: defineStyle({
        border: "none",
        backgroundColor: "transparent",
        padding: 0,
        margin: 0,
        _hover: {
            boxShadow: "none",
            color: "semantic.text.primary",
            _disabled: {
                color: "semantic.text.disabled",
            },
            _loading: {
                background: "semantic.cta.secondaryHover",
            },
        },
        _focusVisible: {
            outline: "none !important",
            color: "semantic.text.primary",
            border: "3px solid",
            borderColor: "semantic.outline.focus",
        },
        _active: {
            color: "semantic.text.primary",
            boxShadow: "none",
        },
        _disabled: {
            color: "semantic.text.disabled",
        },
    }),
    primaryOutlined: defineStyle({
        background: "transparent",
        color: "semantic.text.primary",
        borderColor: "semantic.foreground.primary",
        _loading: {
            borderColor: "semantic.foreground.primary",
        },
        _hover: {
            background: "semantic.cta.primaryHover",
            _disabled: {
                borderColor: "semantic.border.disabled",
                color: "semantic.text.disabled",
            },
            _loading: {
                borderColor: "semantic.foreground.primary",
            },
        },
        _focusVisible: {
            borderColor: "semantic.foreground.primary",
            background: "semantic.cta.primaryHover",
        },
        _active: {
            background: "semantic.cta.secondaryPressed",
            color: "semantic.text.inverse ",
            borderColor: "semantic.cta.primaryPressed",
        },
        _disabled: {
            borderColor: "semantic.border.disabled",
            color: "semantic.text.disabled",
        },
    }),
    secondaryOutlined: defineStyle({
        background: "transparent",
        color: "semantic.cta.secondary",
        borderColor: "semantic.cta.secondary",
        _loading: {
            background: "semantic.cta.secondaryHover",
            borderColor: "semantic.cta.secondary",
        },
        _hover: {
            borderColor: "semantic.interactive.primary",
            background: "semantic.cta.secondaryHover",
            color: "semantic.cta.secondary",
            _disabled: {
                borderColor: "semantic.border.disabled",
                color: "semantic.text.disabled",
            },
            _loading: {
                background: "semantic.cta.secondaryHover",
                borderColor: "semantic.cta.secondary",
            },
        },
        _focusVisible: {
            background: "semantic.cta.secondaryHover",
            color: "semantic.cta.secondary",
        },
        _active: {
            borderColor: "semantic.cta.secondaryPressed",
            background: "semantic.cta.secondaryPressed",
            color: "semantic.text.inverse ",
        },
        _disabled: {
            borderColor: "semantic.border.disabled",
            color: "semantic.text.disabled",
        },
    }),
    primaryOutlinedWhite: defineStyle({
        background: "transparent",
        color: "semantic.text.inverse",
        borderColor: "semantic.border.inverse",
        _loading: {
            borderColor: "semantic.foreground.primary",
        },
        _hover: {
            background: "semantic.cta.primaryHover",
            borderColor: "semantic.foreground.primary",
            color: "semantic.text.primary",
            _disabled: {
                borderColor: "semantic.border.disabled",
                color: "semantic.text.disabled",
            },
            _loading: {
                borderColor: "semantic.foreground.primary",
            },
        },
        _focusVisible: {
            background: "semantic.cta.primaryHover",
            borderColor: "semantic.foreground.primary",
            color: "semantic.text.primary",
            outlineColor: "branding.B2_2",
        },
        _active: {
            borderColor: "semantic.border.inverse",
            background: "semantic.cta.primaryPressed",
            color: "semantic.text.inverse",
        },
        _disabled: {
            borderColor: "semantic.border.disabled",
            color: "semantic.text.disabled",
        },
    }),
    secondaryOutlinedWhite: defineStyle({
        background: "transparent",
        color: "semantic.text.inverse",
        borderColor: "semantic.border.inverse",
        _loading: {
            background: "semantic.cta.secondaryHover",
            borderColor: "semantic.cta.secondary",
        },
        _hover: {
            borderColor: "semantic.interactive.primary",
            background: "semantic.cta.secondaryHover",
            color: "semantic.cta.secondary",
            _disabled: {
                borderColor: "semantic.border.disabled",
                color: "semantic.text.disabled",
            },
            _loading: {
                background: "semantic.cta.secondaryHover",
                borderColor: "semantic.cta.secondary",
            },
        },
        _focusVisible: {
            borderColor: "semantic.interactive.primary",
            background: "semantic.cta.secondaryHover",
            color: "semantic.cta.secondary",
        },
        _active: {
            background: "semantic.cta.secondaryPressed",
            color: "semantic.text.inverse",
            borderColor: "semantic.border.inverse",
        },
        _disabled: {
            borderColor: "semantic.border.disabled",
            color: "semantic.text.disabled",
        },
    }),
    primaryTextOnly: defineStyle({
        border: "none",
        background: "transparent",
        color: "semantic.text.primary",
        _hover: {
            background: "semantic.cta.primaryHover",
            _disabled: {
                color: "semantic.text.disabled",
            },
        },
        _focusVisible: {
            background: "semantic.cta.primaryHover",
        },
        _active: {
            background: "semantic.cta.primaryPressed",
            color: "semantic.text.inverse",
        },
        _disabled: {
            color: "semantic.text.disabled",
        },
    }),
    secondaryTextOnly: defineStyle({
        border: "none",
        background: "transparent",
        color: "semantic.cta.secondary",
        _loading: {
            background: "semantic.cta.secondaryHover",
        },
        _hover: {
            background: "semantic.cta.secondaryHover",
            color: "semantic.text.primary",
            _disabled: {
                color: "semantic.text.disabled",
            },
            _loading: {
                background: "semantic.cta.secondaryHover",
            },
        },
        _focusVisible: {
            background: "semantic.cta.secondaryHover",
        },
        _active: {
            background: "semantic.cta.secondaryPressed",
            color: "semantic.text.inverse",
        },
        _disabled: {
            color: "semantic.text.disabled",
        },
    }),
};
const ButtonTheme = defineStyleConfig({
    baseStyle,
    variants,
    sizes,
    defaultProps: {
        size: "lg",
        variant: ButtonVariant.PRIMARY_FILLED,
    },
});
export default ButtonTheme;
//# sourceMappingURL=Button.default.js.map